import { ServiceEntityType, StatusVariant } from '@consigli/types';
import { ColumnDef } from '@tanstack/react-table';
import { BsThreeDots } from 'react-icons/bs';

import { ActionButton, TableCell } from '@/components';
import { Badge } from '@/components/badge';
import { mapServiceCodeToName } from '@/utils/service-name-mapping';
import { getServiceType, getServiceTypeLabel } from '@/utils/type-guards';
import { Service } from '@/utils/types';

interface ServiceColumnProps {
  onDeleteClick: (service: Service) => void;
  onViewDetailsClick: (service: Service) => void;
  onToggleVisibilityClick: (service: Service) => void;
  onDownloadExcelClick?: (service: Service) => void;
  onDownloadZipClick?: (service: Service) => void;
  isTogglingVisibility: boolean;
  isDeleting: boolean;
}

/**
 * This function will return the appropriate badge based on the status of a service
 * @param service - The service data
 * @returns The status badge
 */
const statusBadge = (service: Service) => {
  const status: string =
    'workflow_step' in service && typeof service.workflow_step === 'string'
      ? service.workflow_step
      : 'Unknown';
  return <Badge text={status} variant={StatusVariant.WARNING} />;
};

/**
 * Create the columns for the service table.
 * @param onDeleteClick - The function to call when the delete button is clicked.
 * @param onViewDetailsClick - The function to call when the view details button is clicked.
 * @param onDownloadZipClick - The function to call when the download zip button is clicked.
 * @param isDeleting - The state of the deleting service.
 * @returns The columns for the service table.
 */
export const createServiceColumns = ({
  onDeleteClick,
  onViewDetailsClick,
  onToggleVisibilityClick,
  onDownloadExcelClick,
  onDownloadZipClick,
  isTogglingVisibility,
  isDeleting,
}: ServiceColumnProps): ColumnDef<Service>[] => [
  {
    header: 'Name',
    accessorKey: 'name',
    cell: ({ row }) => <TableCell>{row.original.name}</TableCell>,
    enableSorting: false,
  },
  {
    header: 'Service',
    accessorKey: 'type',
    cell: ({ row }) => <TableCell>{mapServiceCodeToName(row.original.type)}</TableCell>,
    enableSorting: false,
  },
  {
    header: 'Type',
    cell: ({ row }) => <TableCell>{getServiceTypeLabel(row.original)}</TableCell>,
    enableSorting: false,
  },
  {
    header: 'Status',
    cell: ({ row }) => statusBadge(row.original),
    enableSorting: false,
  },
  {
    accessorKey: 'published',
    header: 'Published',
    cell: ({ row }) => {
      const hasPublished = 'published' in row.original;
      if (!hasPublished) return <TableCell>—</TableCell>;
      const published = (row.original as { published: boolean }).published;
      return (
        <TableCell>
          <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${published ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}
          >
            {published ? 'Published' : 'Unpublished'}
          </span>
        </TableCell>
      );
    },
    enableSorting: false,
  },
  {
    header: '',
    id: 'actions',
    cell: ({ row }) => {
      const serviceType = getServiceType(row.original);
      const actions: Array<{
        label: string;
        onClick: () => void;
        disabled?: boolean;
        destructive?: boolean;
      }> = [
        {
          label: 'View Details',
          onClick: () => onViewDetailsClick(row.original),
        },
      ];

      if (serviceType === ServiceEntityType.DOCUMENT_PACKAGE && onDownloadExcelClick) {
        actions.push({
          label: 'Download document list (Excel)',
          onClick: () => onDownloadExcelClick(row.original),
        });
      }

      if (
        (serviceType === ServiceEntityType.DOCUMENT_PACKAGE ||
          serviceType === ServiceEntityType.BUILDING_PACKAGE) &&
        onDownloadZipClick
      ) {
        actions.push({
          label: 'Download as ZIP',
          onClick: () => onDownloadZipClick(row.original),
        });
      }

      if ('published' in row.original) {
        const published = (row.original as { published: boolean }).published;
        actions.push({
          label: isTogglingVisibility ? 'Updating...' : published ? 'Unpublish' : 'Publish',
          onClick: () => onToggleVisibilityClick(row.original),
          disabled: isTogglingVisibility,
        });
      }

      actions.push({
        label: 'Delete',
        onClick: () => onDeleteClick(row.original),
        disabled: isDeleting || serviceType === ServiceEntityType.BUILDING_PACKAGE, // Building packages cannot be deleted yet. Functionality may be added later.
        destructive: true,
      });

      return <ActionButton icon={<BsThreeDots />} actions={actions} />;
    },
    enableSorting: false,
  },
];
